import { useEffect } from 'react';

import { MessageSquareIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { useTitle } from 'react-use';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { Button } from '@/components/ui/button';
import useBrains from '@/hooks/useBrains';
import useVersions from '@/hooks/useVersions';
import { useBillingState } from '@/modules/billing/hooks/useBillingState';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

import { EmptyVersions } from './EmptyVersions';
import { useVersionsTable } from './useVersionsTable';
import { VersionTimeline } from './VersionsTimeline';

import styles from './Versions.module.scss';

export const Versions = () => {
  useSegments(PageName.VERSIONS);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const slug = useSelector(selectAccountSlug);
  const { maxVersionsReached, versions, maxVersions, versionsWithoutDraft } = useVersions({
    brainId,
  });
  const { brain } = useBrains(brainId);
  const { noVersions, handleCreateVersion } = useVersionsTable();
  const contextualMessagePlaceholder = t('common.virtual_assistant');
  const canWrite = useSelector(
    (state: RootState) => getPermissions(state, 'brains', actions.WRITE),
    shallowEqual
  );
  const billingState = useBillingState();

  const contextualHelp = (
    <ContextualHelp title={t('versions.contextual_title')} name="about_versions">
      {t('versions.contextual_message', { 0: contextualMessagePlaceholder })}
    </ContextualHelp>
  );

  useTitle(t('pages.ai_agent_versions', { 0: brain?.name ?? '-' }));

  const handleClick = () => {
    navigate(`/${slug}/account/billing`, { state: { shouldTriggerAction: Date.now() } });
  };

  // This is a workaround to hide the version tile on AI Agents page
  // We will remove this when we will totally replace Brains with AI Agents
  const location = useLocation();
  const isAIAgentsPath = location.pathname.includes('/ai-agents');

  if (noVersions) {
    return (
      <PageContentWrapper readOnly={!canWrite} noGutters={isAIAgentsPath}>
        <div className="m-6">{contextualHelp}</div>
        <EmptyVersions onCreateVersion={handleCreateVersion} canWrite={canWrite} />
      </PageContentWrapper>
    );
  }

  return (
    <PageContentWrapper readOnly={!canWrite} noGutters={isAIAgentsPath}>
      <div className={styles.dataGridWrapper}>
        {maxVersionsReached && (
          <div className={styles.limitContainer}>
            <span className="text-base leading-6 font-semibold">
              {t('versions.saved_versions')}
            </span>
            <span className="text-sm leading-5 font-normal">
              {t('billing.versions_limits', {
                0: versions?.length - 1,
                1: maxVersions,
                2: billingState.plan_name,
              })}
            </span>
            <Button variant="ghost-neutral" size="sm" onClick={handleClick}>
              <MessageSquareIcon size={16} className="text-muted-foreground" />
              {t('billing.upgrade')}
            </Button>
          </div>
        )}
        {contextualHelp}
        {versionsWithoutDraft?.map((v, i) => (
          <VersionTimeline
            version={v}
            key={v.created}
            isLast={versionsWithoutDraft?.length - 1 === i}
            index={i}
            versions={versionsWithoutDraft}
          />
        ))}
      </div>
    </PageContentWrapper>
  );
};
